import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { FilterForm } from '@/components/filters/FilterForm'

import {
  formatTime,
  getOrganizationOptions,
  getHierarchyOrganizationOptions,
} from '@/utils/helpers'
import Widget from '@/components/widget/Widget'
import { DateRangePickerReusable } from '@/components/datePickers/DateRangePickerReusable'
import Select from '@/components/forms/Select'
import { fetchManagerAudit } from '@/reducers/tasks/tasks.actions'
import { setFilter } from '@/reducers/tasks/tasks.redux'
import { Button } from '@/components/buttons/Button'
import { PageHeader } from '@/components/headers/PageHeader'
import { FormField } from '@/components/forms/FormField'

import { SupervisorUsageWidget } from './SupervisorUsageWidget'

export const SupervisorActivityPage = () => {
  const dispatch = useDispatch()
  const { filters, loading, data } = useSelector((state) => state.tasks || {})
  const { organizationid, hierarchy_manager: isHierarchyManager } = useSelector(
    (state) => state.currentUser
  )
  const { organizations } = useSelector((state) => state)
  const { flattenedUserOrgHierarchy } = useSelector((state) => state.orgHierarchy)

  const isBaltoAdmin = organizationid === 1
  const showManagerHierarchyOrgDropdown =
    !isBaltoAdmin && isHierarchyManager && flattenedUserOrgHierarchy.length > 1
  const hasOrganizationDropdown = isBaltoAdmin || showManagerHierarchyOrgDropdown

  const organizationOptions = isBaltoAdmin
    ? getOrganizationOptions(organizations)
    : getHierarchyOrganizationOptions(flattenedUserOrgHierarchy)

  const handleUpdateRange = (range) => {
    const startDate = formatTime(range.selection.startDate, 'yyyy-MM-DDT00:00:00Z')
    const endDate = formatTime(range.selection.endDate, 'yyyy-MM-DDT23:59:59Z')

    dispatch(setFilter({ startDate, endDate }))
  }

  const handleOrgSelect = (option) => {
    dispatch(setFilter({ organizationId: option?.value || null }))
  }

  const submitFilters = () => {
    dispatch(fetchManagerAudit())
  }

  useEffect(() => {
    dispatch(setFilter({ organizationId: organizationid }))
  }, [])

  useEffect(() => {
    if (filters.organizationId) {
      submitFilters()
    }
  }, [filters.organizationId])

  return (
    <div>
      <PageHeader title="Supervisor Activity" />

      <FilterForm
        small
        sticky={false}
        inlineActions
        actions={
          <Button
            brand
            onClick={submitFilters}
            disabled={loading.managerAudit}
            data-testid="submit-filters-button"
          >
            Filter
          </Button>
        }
      >
        {hasOrganizationDropdown && (
          <FormField data-testid="organization-dropdown">
            <label>Organization</label>
            <Select
              options={organizationOptions}
              value={filters.organizationId}
              onChange={handleOrgSelect}
              placeholder="Select Organization"
              isClearable={false}
              fixedWidth
            />
          </FormField>
        )}
        <FormField>
          <label>Date Range</label>
          <DateRangePickerReusable
            data-testid="date-range-picker"
            onChange={handleUpdateRange}
            startDate={filters.startDate}
            endDate={filters.endDate}
            saveOrCloseOnClickPreset
          />
        </FormField>
      </FilterForm>

      <Widget
        widgetId="usage-supervisor"
        label="Supervisor Usage Report"
        subtitle="Supervisor activity in the selected date range"
        tableOnly
        showCsvDownload
      >
        <SupervisorUsageWidget
          statusKeys={[
            'created',
            'sent_to_agent',
            'acknowledged',
            'calls_viewed',
            'coaching_calls_viewed',
            'kodi_sessions',
          ]}
          data={data.managerAudit || []}
          loading={loading.managerAudit}
          paginationPageSize={20}
        />
      </Widget>
    </div>
  )
}
